<template>
  <div ref="root" class="ak-modal-container">
    <transition
      name="fade"
      @after-enter="afterEnter"
      @before-leave="beforeLeave"
      @enter="onEnter"
    >
      <div
        v-if="isActive"
        v-trap-focus="trapFocus"
        :class="dialogClass"
        :role="ariaRole"
        :aria-modal="ariaModal"
        class="ak-modal"
        :style="zIndex"
        @click.self="cancel('outside')"
      >
        <transition :name="animation">
          <ak-form
            v-show="showContent"
            class="ak-modal__dialog"
            :class="dialogSize"
            @submit="confirm"
          >
            <ak-close
              v-if="showX"
              class="ak-modal__close"
              @click.prevent="cancel('x')"
            />
            <div v-if="hasIcon && iconType" class="ak-modal__body">
              <ak-modal-title v-if="title" :color="type">{{
                title
              }}</ak-modal-title>
              <ak-pack spacing="small" align="center">
                <ak-pack-item :class="typeTextColor" shrink align="top">
                  <ak-icon :type="iconType" size="huge" />
                </ak-pack-item>
                <ak-pack-item grow>
                  <div class="text-large" v-html="message" />
                  <ak-field
                    v-if="hasInput"
                    ref="field"
                    :message="validationMessage"
                    class="mt-tiny"
                  >
                    <ak-input
                      ref="input"
                      v-model="prompt"
                      grow
                      :class="{ danger: validationMessage }"
                      v-bind="inputAttrs"
                      @keyup.enter="confirm"
                    />
                  </ak-field>
                </ak-pack-item>
              </ak-pack>
            </div>

            <div v-else class="ak-modal__body">
              <ak-modal-title v-if="title" :color="type"
                >{{ title }}
              </ak-modal-title>
              <div class="text-large" v-html="message" />
              <ak-field
                v-if="hasInput"
                ref="field"
                class="mt-tiny"
                :message="validationMessage"
              >
                <ak-input
                  ref="input"
                  v-model="prompt"
                  class="input"
                  grow
                  :class="{ danger: validationMessage }"
                  v-bind="inputAttrs"
                  @keyup.enter="confirm"
                />
              </ak-field>
            </div>

            <div class="ak-modal__footer">
              <ak-modal-button
                v-if="showCancel && cancelText"
                ref="cancelButton"
                size="large"
                color="grey-lightest"
                class="ak-modal__footer-item"
                @click="cancel('button')"
              >
                {{ cancelText }}
              </ak-modal-button>
              <ak-modal-button
                ref="confirmButton"
                size="large"
                :color="type"
                class="ak-modal__footer-item"
                type="submit"
              >
                {{ confirmText }}
              </ak-modal-button>
            </div>
          </ak-form>
        </transition>
      </div>
    </transition>
  </div>
</template>

<script>
import { defineComponent, ref, computed } from 'vue'
import { modalProps, dialogProps, modalMixin } from '../../mixins/modalMixin'
import trapFocus from '../../directives/trapFocus'
import Icon from '../icon/Icon.vue'
import Form from '../form/Form.vue'
import ModalButton from '../modal/ModalButton.vue'
import ModalTitle from '../modal/ModalTitle.vue'
import Pack from '../pack/Pack.vue'
import PackItem from '../pack/PackItem.vue'
import Field from '../field/Field.vue'
import Input from '../input/Input.vue'
import Button from '../button/Button.vue'
import Close from '../close/Close.vue'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-dialog />
   */
  name: 'AkDialog',

  /**
   * @type {Object}
   * @description A collection of components that this component
   * is dependant on.
   */
  components: {
    [Icon.name]: Icon,
    [ModalButton.name]: ModalButton,
    [ModalTitle.name]: ModalTitle,
    [PackItem.name]: PackItem,
    [Pack.name]: Pack,
    [Field.name]: Field,
    [Input.name]: Input,
    [Button.name]: Button,
    [Form.name]: Form,
    [Close.name]: Close,
  },

  /**
   * @type {Object}
   * @description A collection of directives that this component is
   * dependant on.
   */
  directives: {
    trapFocus,
  },

  /**
   * @type {Boolean}
   * @description This prevents all element attributes that are not
   * defined in the props object from being applied to the rendered
   * component root element, the exception to this is "class" all
   * of the given classes would be merged with the component
   * defined ones.
   */
  inheritAttrs: false,

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    ...modalProps,
    ...dialogProps,
  },

  /**
   * @type {Array}
   * @description The list of events that this component can trigger
   */
  emits: ['close', 'update:active'],

  /**
   * @param {Object} props - The properties of the component
   * @param {Object} options - The setup options
   * @param {Function} options.emit - The emit method for triggering events
   * @param {Function} options.attrs - The attributes object for the component
   * @description Sets up the component and returns an object containing all of the
   * attributes for the component to use.
   */
  setup(props, { emit, attrs }) {
    const root = ref(null)
    const input = ref(null)
    const field = ref(null)
    const cancelButton = ref(null)
    const confirmButton = ref(null)
    const validationMessage = ref('')
    const hasValidationError = ref(false)
    const prompt = ref(props.hasInput ? input.value : '')

    const mixin = modalMixin({
      type: 'dialog',
      props,
      emit,
      attrs,
      refs: {
        root,
        input,
        field,
        cancelButton,
        confirmButton,
      },
    })

    /**
     * @returns {Array}
     * @description Returns an array of classes to apply to the dialog
     */
    const dialogClass = computed(() => {
      return [
        props.size,
        {
          'modal__dialog--has-custom-container': props.container !== null,
        },
      ]
    })

    const dialogSize = computed(() => {
      return {
        [`ak-modal__dialog--${props.size}`]: props.size,
      }
    })

    /**
     * @returns {String|Null}
     * @description Icon name (MDI) based on the type.
     */
    const iconByType = computed(() => {
      switch (props.type) {
        case 'info':
          return 'information-circled'
        case 'success':
          return 'checkmark'
        case 'warning':
          return 'android-alert'
        case 'danger':
          return 'close-circled'
        default:
          return null
      }
    })

    /**
     * @return {String}
     * @description Returns the text color to display
     */
    const typeTextColor = computed(() => {
      switch (props.type) {
        case 'danger':
          return 'color-danger'
        case 'success':
          return 'color-success'
        case 'warning':
          return 'color-warning'
        default:
          return 'color-primary'
      }
    })

    /**
     * @return {String}
     * @description Returns the icon type to display
     */
    const iconType = computed(() => {
      return props.icon || props.iconByType
    })

    /**
     * @description If it's a prompt Dialog, validate the input. Call the
     * onConfirm prop (function) and close the Dialog.
     */
    const confirm = () => {
      props.onConfirm(prompt.value)
      mixin.close()
    }

    return {
      ...mixin,
      root,
      input,
      field,
      cancelButton,
      confirmButton,
      attrs,
      prompt,
      validationMessage,
      hasValidationError,
      dialogClass,
      iconByType,
      typeTextColor,
      iconType,
      confirm,
      dialogSize,
    }
  },
})
</script>
